/**
 * Unleash feature flag SDK configuration.
 *
 * Backed by the Unleash UI at https://admin.jdsimplified.com/feature-flags/.
 * Frontend evaluates flags via the proxy API; the `clientKey` is a frontend
 * token (default:production scope), which is browser-safe and CORS-allowed
 * from jdsimplified.com domains + localhost:5173/5174.
 *
 * Consumer pattern:
 *
 *   import { useFlag } from '@unleash/proxy-client-react';
 *   const enabled = useFlag('community-rollout');
 *
 * User-targeting context (Firebase UID) is pushed by `UnleashContextSync`
 * in main.tsx so per-user rollouts key off the JDS user_id (which equals
 * the Firebase UID and profiles.id).
 *
 * Defensive guard: `unleashEnabled` is the load-bearing boolean for whether
 * the SDK mounts at all. If either VITE_UNLEASH_URL or
 * VITE_UNLEASH_FRONTEND_TOKEN is missing at build time, Vite inlines them as
 * `undefined`, and the SDK throws `Error: url is required` synchronously
 * inside `<FlagProvider>` — which takes the whole React tree down. Callers
 * MUST check `unleashEnabled` before mounting `<FlagProvider>` so a missing
 * secret is a no-op (flags resolve to default), not a blank screen.
 */

import type { IConfig } from '@unleash/proxy-client-react';

/**
 * A per-request time limit for the Unleash SDK's flag fetch, covering the
 * WHOLE request: headers AND body.
 *
 * WHY (live smoke 2, 2026-10-01: the Ask JDS tab missing for 30 s and 75 s after
 * a reload, flags still loading): unleash-proxy-client 3.8 has no request
 * timeout. `start()` awaits the INITIAL fetch before it schedules polling, so
 * one hung request means no poll is ever scheduled and the flags never
 * resolve for the life of the page. A request that stalls AFTER its headers
 * hangs the same way, because the SDK then awaits `response.json()` (review
 * a0fd6d09b5b575dd5, proven against the real SDK): so the body is read here,
 * inside the limit, and handed on as a complete Response.
 *
 * The limit ESCALATES after each timeout (8 s, 12 s, 16 s) and stays at the
 * level that last succeeded, so a flag server that is consistently slow still
 * loads, just late; past the 15 s poll interval the SDK's own next poll is
 * what ends a request. A timeout is raised as a TimeoutError (not an
 * AbortError, which the SDK swallows as its own), so the SDK logs it and
 * reports flagsError. The SDK's own abort signal still wins.
 */
export const UNLEASH_FETCH_TIMEOUTS_MS: readonly number[] = [8_000, 12_000, 16_000];

const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

export function createUnleashFetch(base: typeof fetch, timeoutsMs: readonly number[] = UNLEASH_FETCH_TIMEOUTS_MS): typeof fetch {
  let level = 0;
  return async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const timeoutMs = timeoutsMs[Math.min(level, timeoutsMs.length - 1)];
    const controller = new AbortController();
    const outer = init.signal;
    const onOuterAbort = () => controller.abort(outer?.reason);
    if (outer) {
      if (outer.aborted) controller.abort(outer.reason);
      else outer.addEventListener('abort', onOuterAbort, { once: true });
    }
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    try {
      const res = await base(input, { ...init, signal: controller.signal });
      // Read the body INSIDE the limit: the SDK's response.json() would otherwise wait on a stalled body forever.
      const body = NULL_BODY_STATUSES.has(res.status) ? null : await res.text();
      return new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
    } catch (err) {
      if (timedOut) {
        level = Math.min(level + 1, timeoutsMs.length - 1);
        throw new DOMException(`Unleash flag fetch exceeded ${timeoutMs} ms`, 'TimeoutError');
      }
      throw err;
    } finally {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onOuterAbort);
    }
  };
}

const url = import.meta.env.VITE_UNLEASH_URL;
const clientKey = import.meta.env.VITE_UNLEASH_FRONTEND_TOKEN;

export const unleashEnabled: boolean = !!url && !!clientKey;

/**
 * Typed config — only safe to pass to `<FlagProvider>` when `unleashEnabled`
 * is true. The non-null assertions are validated by the boolean above.
 */
export const unleashConfig: IConfig = {
  url: url!,
  clientKey: clientKey!,
  refreshInterval: 15, // seconds — Unleash recommends 15-30
  // Bound every flag request (see createUnleashFetch). Resolved lazily so tests and SSR without fetch still load.
  fetch: typeof fetch === 'function' ? createUnleashFetch((...args: Parameters<typeof fetch>) => fetch(...args)) : undefined,
  appName: 'jds-frontend',
  environment:
    import.meta.env.MODE === 'production' ? 'production' : 'development',
};
